import { getScoringHistoryExportData } from '../api/scoringApi';
import {
  buildMultiRecordHandoffState,
  SCORING_HANDOFF_MAX_RECORDS,
} from '../pages/scoring/scoringSaenggibuHandoff';
import { calculateNEISBytes } from './neisBytes';
import { buildEvidenceItems, buildMemoEvidenceLines, MEMO_AREAS, memosForCategory, profileForCategory } from './studentMemos';

// 서버 normalize_name과 같다: 괄호 안·공백·기호를 빼고 비교한다.
export const nameKey = (name) => String(name || '')
  .normalize('NFC')
  .replace(/\([^)]*\)|\[[^\]]*\]/g, '')
  .replace(/[^0-9A-Za-z가-힣]/g, '')
  .toLowerCase();

// 명단 이름·별칭 → 명단 이름. 명단 안에서 같은 이름(동명이인)은 묶지 않는다.
export const rosterCanonicalName = (rosterStudents = []) => {
  const byKey = new Map();
  const taken = new Set();
  rosterStudents.forEach((student, index) => {
    const id = student.sid || `index_${index}`;
    [student.name, ...(student.aliases || [])].forEach((value) => {
      const key = nameKey(value);
      if (!key) return;
      if (byKey.has(key) && byKey.get(key).id !== id) taken.add(key);
      else byKey.set(key, { id, name: student.name });
    });
  });
  return name => {
    const key = nameKey(name);
    return key && !taken.has(key) ? byKey.get(key)?.name || null : null;
  };
};

// 고른 학생들의 채점 기록 id. 최근 기록부터 생기부 핸드오프 한도까지.
export const pickScoringRecordIds = (students = []) => {
  const latest = new Map();
  students.forEach(student => (student.items || []).forEach((item) => {
    if (item.kind !== 'scoring' || !item.record_id) return;
    const date = item.date || '';
    if (!latest.has(item.record_id) || latest.get(item.record_id) < date) latest.set(item.record_id, date);
  }));
  return [...latest.entries()]
    .sort((a, b) => (a[1] < b[1] ? 1 : a[1] > b[1] ? -1 : 0))
    .slice(0, SCORING_HANDOFF_MAX_RECORDS)
    .map(([id]) => id);
};

export const hasScoringRecords = student => (student?.counts?.scoring || 0) > 0;

/** 채점 결과 중 생기부 근거로 쓸 수 있는 것(빈 답안·한 줄 없는 결과 제외). */
export const scoringEvidence = student => (student?.items || []).filter(item => item.kind === 'scoring' && !item.blank && item.line);

/**
 * 학생별 기록 → /create-saenggibu 다중 생성으로 넘길 batchRestore.
 * 채점 결과(점수 문장 뺀 관찰 내용)를 학생마다 모으고, 고른 학생만 남겨 명단 학생 id를 붙인다.
 */
export const buildRosterSaenggibuHandoff = async ({ roster, students }) => {
  const ids = pickScoringRecordIds(students);
  if (ids.length === 0) return null;
  const response = await getScoringHistoryExportData(ids);
  const records = Array.isArray(response?.records) ? response.records : [];
  const handoff = buildMultiRecordHandoffState(records, { canonicalName: rosterCanonicalName(roster?.students) });
  if (!handoff) return null;
  const wanted = new Map(students.map(student => [nameKey(student.name), student.sid]));
  const kept = handoff.batchRestore.students
    .filter(student => wanted.has(nameKey(student.name)))
    .map((student, index) => ({ ...student, id: `roster_${index + 1}`, roster_sid: wanted.get(nameKey(student.name)) }));
  if (kept.length === 0) return null;
  const restore = handoff.batchRestore;
  return {
    ...restore,
    students: kept,
    sourceFileName: `${roster?.label || '우리 반'} 학생별 기록`,
    sourceFileMeta: { ...restore.sourceFileMeta, entry: 'student_records', student_count: kept.length },
    structureAnalysis: { ...restore.structureAnalysis, total_students: kept.length },
  };
};

export const CLASS_RECORDS_SOURCE = 'class_records';
const MAX_STUDENT_CHARS = 15000;
const MEMO_HEAD = '선생님 관찰 메모';
const SCORING_HEAD = '서술형 채점 결과';

/** 이 영역으로 반 전체를 쓸 때 학생마다 넣을 메모 수·채점 수. 생기부에 쓸 수 없어 넘기지 않는 메모는 세지 않는다. */
export const classEvidenceCounts = ({ students = [], notesBySid = {}, category = '행동발달', includeScoring = false }) => students
  .map(student => ({
    sid: student.sid,
    memos: buildEvidenceItems({ memos: notesBySid[student.sid]?.memos || [] }, { category }).length,
    scoring: includeScoring ? scoringEvidence(student).length : 0,
  }));

/** 이 영역에 채점 결과를 처음부터 넣을지: 학생 한 명씩 쓸 때와 같이 교과세특만 켠다. */
export const defaultIncludeScoring = category => category === '교과';

const ownedNames = student => [student?.name, ...(student?.aliases || [])];

/**
 * 메모에 다른 학생 이름이 나와도 지운다. 짧은 별칭(두 글자)은 보통 낱말과 겹칠 수 있어
 * 그 학생 자신의 메모에서만 지우고, 다른 학생은 명단 이름만 지운다.
 */
const maskTargets = (student, rosterStudents) => [
  ...ownedNames(student),
  ...rosterStudents.map(item => item?.name),
];

const fitLines = (lines, budget) => {
  const kept = [];
  let used = 0;
  for (const line of lines) {
    const next = used + line.length + 1;
    if (next > budget) break;
    kept.push(line);
    used = next;
  }
  return kept;
};

const fitSections = (text, budget) => {
  const sections = String(text || '').split(/\n\n(?=\[)/);
  let out = '';
  for (const section of sections) {
    const next = out ? `${out}\n\n${section}` : section;
    if (next.length > budget) break;
    out = next;
  }
  return out;
};

/** 채점 결과를 명단 학생(sid)에 붙인다. 한 이름이 둘 이상(동명이인·같은 기록에 둘)이면 붙이지 않는다. */
const scoringBySid = (handoffStudents, students) => {
  const sidByKey = new Map();
  const doubtful = new Set();
  students.forEach((student) => {
    const key = nameKey(student.name);
    if (!key) return;
    if (sidByKey.has(key)) doubtful.add(key);
    sidByKey.set(key, student.sid);
  });
  const seen = new Map();
  handoffStudents.forEach((item) => {
    const key = nameKey(item.name);
    if (!key) return;
    if (seen.has(key)) doubtful.add(key);
    seen.set(key, item);
  });
  const out = new Map();
  seen.forEach((item, key) => {
    if (doubtful.has(key) || !sidByKey.has(key)) return;
    out.set(sidByKey.get(key), item);
  });
  return out;
};

/**
 * 다중 생성 화면에서 학급 관리로 가져온 학생 글을 얼마나 고쳤는지(본문은 보내지 않는다).
 * revised: 처음 넘어온 글과 달라진 학생, removed: 목록에서 뺀 학생.
 */
export const classEvidenceReviewCounts = (original = [], current = []) => {
  const before = new Map(original.map(student => [student.id, String(student.existing_content ?? student.content ?? '')]));
  const kept = current.filter(student => before.has(student.id));
  return {
    revised: kept.filter(student => String(student.existing_content ?? student.content ?? '') !== before.get(student.id)).length,
    removed: before.size - new Set(kept.map(student => student.id)).size,
  };
};

export const CLASS_TARGET_MAX_BYTES = 1500;
const CLASS_TARGET_MIN_BYTES = 500;
// 근거보다 훨씬 길게 쓰게 하면 AI가 분량을 채우려고 없는 활동·역할을 덧붙인다. 근거의 1.4배 안팎에서 가장 덜 지어낸다.
const CLASS_TARGET_PER_EVIDENCE = 1.4;
const MEMO_LINE_HEAD = new RegExp(`^- (?:\\d{1,2}/\\d{1,2} · )?(?:(?:${[...MEMO_AREAS, '채점'].join('|')}) · )?(?:\\(여러 명이 함께\\) )?`);

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

/** 학생 한 명의 근거 분량(날짜·영역 표시 뺀 메모·고정 정보·채점 결과). */
export const evidenceBytes = (fixed = [], memoLines = [], scoringText = '') => calculateNEISBytes(
  [...fixed, ...memoLines.map(line => line.replace(MEMO_LINE_HEAD, ''))].join(' '),
) + calculateNEISBytes(scoringText);

/** 반 전체 목표 바이트: 학생 근거 분량 중앙값의 1.4배를 100B 단위로 올려 500~1500B. */
export const classTargetBytes = (perStudentBytes = []) => {
  if (perStudentBytes.length === 0) return CLASS_TARGET_MAX_BYTES;
  const target = Math.ceil((median(perStudentBytes) * CLASS_TARGET_PER_EVIDENCE) / 100) * 100;
  return Math.min(CLASS_TARGET_MAX_BYTES, Math.max(CLASS_TARGET_MIN_BYTES, target));
};

export const buildClassEvidenceInstruction = ({ hasMemos = false, hasGroupMemos = false, scoringInstruction = '' } = {}) => [
  hasMemos
    ? '아래 학생별 내용은 선생님이 학기 중에 남긴 관찰 메모입니다. 메모에 적힌 사실만 근거로 쓰고, 없는 활동이나 결과는 만들지 마세요.'
    : '',
  hasMemos ? '메모 앞의 날짜와 영역 표시는 문장에 쓰지 마세요.' : '',
  hasMemos
    ? '메모가 짧아도 분량을 채우려고 메모에 없는 활동·역할·결과·태도 변화를 덧붙이지 말고, 메모에 적힌 사실을 구체적으로 풀어 쓰세요.'
    : '',
  hasGroupMemos
    ? '"(여러 명이 함께)"가 붙은 활동은 같은 반 다른 학생에게도 들어 있으니, 이 학생이 맡은 일과 보인 모습을 중심으로 학생마다 다르게 쓰세요.'
    : '',
  scoringInstruction,
].filter(Boolean).join(' ');

/**
 * 학급 관리 반 화면 → 생기부 다중 생성. 학생마다 고른 영역의 메모(이름 지움, 생기부에 쓸 수 없는 문장 뺌)와
 * 임원·진로희망, 고르면 채점 결과를 한 글로 묶는다. 근거가 하나도 없는 학생은 뺀다.
 * 근거는 한 줄에 하나라서 다중 생성 화면의 학생 카드에서 줄을 지우면 그 근거만 빠진다.
 */
export const buildClassEvidenceHandoff = async ({
  roster,
  students = [],
  notesBySid = {},
  category = '행동발달',
  includeScoring = false,
} = {}) => {
  const rosterStudents = roster?.students || [];
  let scoring = new Map();
  let scoringInstruction = '';
  let scoringRecordCount = 0;
  if (includeScoring) {
    const ids = pickScoringRecordIds(students.filter(student => scoringEvidence(student).length > 0));
    if (ids.length > 0) {
      const response = await getScoringHistoryExportData(ids);
      const records = Array.isArray(response?.records) ? response.records : [];
      const handoff = buildMultiRecordHandoffState(records, { canonicalName: rosterCanonicalName(rosterStudents) });
      if (handoff) {
        scoring = scoringBySid(handoff.batchRestore.students, students);
        scoringInstruction = handoff.batchRestore.teacherInstruction;
        scoringRecordCount = handoff.summary?.records || 0;
      }
    }
  }

  let memoCount = 0;
  let hasGroupMemos = false;
  let usedScoring = 0;
  const perStudentBytes = [];
  const built = students.map((student) => {
    const notes = notesBySid[student.sid] || {};
    const memos = memosForCategory(notes.memos || [], category);
    const { fixed, evidence } = buildMemoEvidenceLines(
      { profile: profileForCategory(notes.profile, category), memos },
      { category, names: maskTargets(student, rosterStudents) },
    );
    const scored = scoring.get(student.sid);
    if (evidence.length === 0 && !scored) return null;

    const head = fixed.join('\n');
    const memoLines = fitLines(evidence, MAX_STUDENT_CHARS - head.length - MEMO_HEAD.length - 4);
    const parts = [head, memoLines.length > 0 ? `${MEMO_HEAD}\n${memoLines.join('\n')}` : ''].filter(Boolean);
    let content = parts.join('\n\n');
    let scoringText = '';
    if (scored) {
      const room = MAX_STUDENT_CHARS - content.length - SCORING_HEAD.length - 3;
      scoringText = fitSections(scored.existing_content, room);
      if (scoringText) content = [content, `${SCORING_HEAD}\n${scoringText}`].filter(Boolean).join('\n\n');
    }
    if (memoLines.length === 0 && !content.includes(SCORING_HEAD)) return null;
    perStudentBytes.push(evidenceBytes(fixed, memoLines, scoringText));

    const warnings = [...(scored?.warnings || [])];
    if (memoLines.length < evidence.length) warnings.push(`메모 ${evidence.length - memoLines.length}개는 길이 제한으로 뺐어요`);
    memoCount += memoLines.length;
    if (memoLines.some(line => line.includes('(여러 명이 함께)'))) hasGroupMemos = true;
    if (content.includes(SCORING_HEAD)) usedScoring += 1;
    return {
      name: student.name,
      roster_sid: student.sid,
      existing_content: content,
      content,
      characteristics: scored?.characteristics || '',
      confidence: warnings.length > 0 ? 0.6 : 1,
      warnings,
    };
  });

  const kept = built.filter(Boolean).map((student, index) => ({ id: `class_${index + 1}`, ...student }));
  if (kept.length === 0) return null;
  return {
    source: CLASS_RECORDS_SOURCE,
    category,
    wordCount: classTargetBytes(perStudentBytes),
    students: kept,
    teacherInstruction: buildClassEvidenceInstruction({
      hasMemos: memoCount > 0,
      hasGroupMemos,
      scoringInstruction: usedScoring > 0 ? scoringInstruction : '',
    }),
    referenceText: '',
    sourceFileName: `${roster?.label || '우리 반'} 학생별 기록`,
    sourceFileSize: 0,
    sourceFileMeta: {
      source: CLASS_RECORDS_SOURCE,
      entry: CLASS_RECORDS_SOURCE,
      category,
      student_count: kept.length,
      skipped_count: students.length - kept.length,
      memo_count: memoCount,
      scoring_student_count: usedScoring,
      ...(usedScoring > 0 ? { scoring_record_count: scoringRecordCount } : {}),
    },
    structureAnalysis: {
      total_students: kept.length,
      detected_columns: ['학생명', ...(memoCount > 0 ? [MEMO_HEAD] : []), ...(usedScoring > 0 ? [SCORING_HEAD] : [])],
    },
  };
};
